/**
 * Form class that contains multiple elements.
 * Provides methods to manage elements and validate the form.
 */
import { Element } from './Element'
import { SectionElement } from './elements/SectionElement'

const AllowedParamTypes = new Set(['string', 'number', 'boolean'])

function isValidParam<T>(value: unknown, defaultValue?: T): value is T {
  //if we have a default value, param is valid if the same type
  if (defaultValue) {
    return typeof value === typeof defaultValue
  }

  //we don't have default value, param is valid if is string, number or boolean
  return AllowedParamTypes.has(typeof value)
}

export class Form {
  /**
   * Unique identifier for the form
   */
  id: string

  /**
   * The title of the form
   */
  title: string

  /**
   * Description of the form
   */
  description: string | null

  /**
   * Collection of elements in the form
   */
  elements: Element[]

  /**
   * Custom parameters specific to the form
   */
  params: Record<string, unknown>

  /**
   * Type of the form resource
   */
  resourceType: string

  /**
   * Id of the form resource
   */
  resourceId: string

  /**
   * Constructor for the Form class
   *
   * @param id - Unique identifier for the form
   * @param resourceId - Id of the form resource
   * @param resourceType - Type of the form resource
   * @param title - The title of the form
   * @param description - Description of the form
   * @param elements - Initial collection of elements
   * @param params - Custom parameters specific to the form
   */
  constructor(
    id: string,
    title: string,
    description: string | null,
    resourceType: string,
    resourceId: string,
    elements: Element[] = [],
    params: Record<string, unknown> = {}
  ) {
    this.id = id
    this.title = title
    this.description = description
    this.elements = elements
    this.params = params
    this.resourceType = resourceType
    this.resourceId = resourceId

    // Sort elements by position
    this.sortElements()
  }

  /**
   * Add an element to the form
   *
   * @param element - The element to add
   */
  addElement(element: Element): void {
    element.sectionId = null
    element.position = this.elements.length
    this.elements.push(element)
    this.sortElements()
  }

  /**
   * Remove an element from the form
   *
   * @param elementId - The ID of the element to remove
   */
  removeElement(elementId: string): void {
    this.elements = this.elements.filter((element) => element.id !== elementId)
    this.updatePositions()
  }

  /**
   * Get an element by its ID
   *
   * @param elementId - The ID of the element to get
   * @returns The element with the specified ID, or undefined if not found
   */
  getElement(elementId: string): Element | null {
    for (const element of this.elements) {
      if (element.id === elementId) {
        return element
      }

      if (element instanceof SectionElement) {
        for (const child of element.elements) {
          if (child.id === elementId) {
            return child
          }
        }
      }
    }
    return null
  }

  /**
   * Sort elements by their position
   */
  sortElements(): void {
    this.elements.sort((a, b) => a.position - b.position)
  }

  /**
   * Update positions of all elements to ensure they are sequential
   */
  updatePositions(): void {
    this.elements.forEach((element, index) => {
      element.position = index
    })
  }

  /**
   * Validate all elements in the form
   *
   * @returns True if all elements are valid, false otherwise
   */
  validate(): boolean {
    // Check if there are any elements
    if (this.elements.length === 0) {
      return false
    }

    // Check if the title is not empty
    if (!this.title.trim()) {
      return false
    }

    // Validate each element
    for (const element of this.elements) {
      if (!element.validate()) {
        return false
      }
    }

    return true
  }

  /**
   * Get a custom parameter value
   *
   * @param key - The key of the custom parameter
   * @param defaultValue - The default value to return if the parameter doesn't exist
   */
  getParam<T>(key: string, defaultValue?: T): T | undefined {
    const value = this.params[key]

    if (key in this.params && isValidParam<T>(value, defaultValue)) {
      return value
    }

    return defaultValue
  }

  /**
   * Set a custom parameter value
   *
   * @param key - The key of the custom parameter
   * @param value - The value to set
   */
  setParam<T>(key: string, value: T): void {
    this.params[key] = value
  }

  /**
   * Deletes a custom parameter value
   *
   * @param key - The key of the custom parameter
   */
  deleteParam(key: string): void {
    delete this.params[key]
  }

  /**
   * Get all elements of a specific type
   *
   * @param type - The type of elements to get
   * @returns An array of elements of the specified type
   */
  getElementsByType(type: string): Element[] {
    return this.elements.filter((element) => element.getType() === type)
  }

  /**
   * Get the number of elements in the form
   */
  getElementCount(sectionId?: string): number {
    if (sectionId) {
      return this.elements.filter((el) => el.sectionId === sectionId).length
    } else {
      return this.elements.length
    }
  }

  /**
   * Get the number of mandatory elements in the form
   */
  getMandatoryElementCount(): number {
    return this.elements.filter((element) => element.mandatory).length
  }
}
